import { sendTrackEvent } from "@sinch/bus";
import { REQUEST_TOKEN_PLACEHOLDER } from "@sinch/cache-helpers";
import { EVENT_NAME } from "../analytics/rudderstack/createRudderstackEvent";
import { matchPath } from "../components/Router/utils";

import { getMessageMediaApiUrlMiddleware } from "../config/config";
import {
  getLocalStorage,
  isStringOrNull,
  listenToLocalStorageChange,
  REFRESH_TOKEN,
  setLocalStorage,
} from "../helpers/localStorage";
import { APP_NAME } from "../portals/constants";
import { loginHubPaths } from "../portals/messagemedia/navigationConfig";
import type { AuthStrategy, Token } from "./types";

export type MMUserSessionType = {
  accessToken: string;
  refreshToken: string;
  expireIn: number;
  createdAt: number;
};

type MMUserRefreshResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
};

export const tokenSecondsLeft = (session: MMUserSessionType) =>
  session.expireIn - (Math.floor(Date.now() / 1000) - session.createdAt);

const { fetch: originalFetch } = window;

const urlSymbol = Symbol("url");
const tokenHeader = Symbol("tokenHeader");

declare global {
  interface XMLHttpRequest {
    [urlSymbol]?: string | URL;
    [tokenHeader]?: [string, string];
  }
}

export class MessageMediaAuthStrategy implements AuthStrategy {
  /**
   * Locking mechanism to make sure multiple instances of the useMMToken hook
   * refreshing the token at once will use only one Promise
   */
  accessTokenUpdatePromise: Promise<Partial<MMUserSessionType>> | undefined;

  isPatchingRefreshToken = false;

  userSession: MMUserSessionType | undefined;

  init = (): Promise<boolean> => {
    const isAuthScreen = () =>
      loginHubPaths.some((authPath) => matchPath(authPath, location.pathname));

    this.getSession().then((session) => {
      if (!session && !isAuthScreen()) {
        history.pushState(
          "",
          "",
          `/login?redirect=${encodeURIComponent(location.pathname)}`
        );
      }
    });
    return Promise.resolve(true);
  };

  login = () => {
    // We don't need to do anything here, as the login is handled by the
    // MessageMedia login page.
    console.log("to be implemented");
  };

  logout = () => {
    sendTrackEvent({
      name: EVENT_NAME.USER_LOGOUT,
      appName: APP_NAME.toLowerCase(),
      purposes: ["Analytical"],
    });

    window.history.pushState(history.state, "", "/logout");
  };

  getToken = () =>
    this.userSession?.accessToken
      ? {
          token: REQUEST_TOKEN_PLACEHOLDER,
          parsedToken: {}, // TODO: parse token
        }
      : null;

  getTokenP = async () => {
    const token = await this.getSession().then(() => this.getToken());
    if (!token) {
      throw new Error("Couldn't retrieve token");
    }
    return token;
  };

  patchRefreshToken: AuthStrategy["patchRefreshToken"] = async (
    { accountId, onPatchSuccess, redirectUrl = "/" },
    options
  ) => {
    if (this.isPatchingRefreshToken) {
      return;
    }
    this.isPatchingRefreshToken = true;
    const refresh_token = this.getSavedToken();

    if (!refresh_token) {
      console.warn("No Refresh token found! Have you logged in?");
      return;
    }

    const body = JSON.stringify({
      grant_type: "refresh_token",
      refresh_token,
      ...options,
      ...(accountId ? { accountId } : {}),
    });

    const response = await originalFetch(
      `${getMessageMediaApiUrlMiddleware()}/iam/v1/jwt/auth`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body,
      }
    );

    if (!response.ok) {
      this.isPatchingRefreshToken = false;
      if (response.status === 401)
        throw new Error("Couldn't retrieve refresh token: not-authenticated");
      throw new Error(
        `Couldn't retrieve refresh token ${await response.text()}`
      );
    }

    const newLoginData = await this.handleAuthResponse(response);
    // Only place where the shell can write the refresh token
    setLocalStorage(REFRESH_TOKEN, newLoginData.refresh_token);
    onPatchSuccess?.();

    window.location.assign(redirectUrl);
  };

  onTokenUpdate = (callback: (token: Token | null) => void) =>
    listenToLocalStorageChange(REFRESH_TOKEN, () => {
      const newRefreshToken = localStorage.getItem(REFRESH_TOKEN);
      if (newRefreshToken) {
        this.fetchNewToken(this.parseToken(newRefreshToken))?.then(() =>
          callback(this.getToken())
        );
      } else {
        this.userSession = undefined;
      }
    });

  patchRequests = () => {
    const getCurrentUserSession = () => this.userSession;
    const getCurrentUserSessionAsync = async () => await this.getSession();

    const matchUrlForToken = (req: Request | URL | string) => {
      const apiHostname = new URL(getMessageMediaApiUrlMiddleware()).hostname;
      const hostname = (
        req instanceof URL
          ? req
          : new URL(req instanceof Request ? req.url : req)
      ).hostname;

      return (
        hostname.endsWith(apiHostname) ||
        hostname.endsWith(".messagemedia.com") ||
        hostname.endsWith(".sinch.com") ||
        hostname.endsWith(".messengerpeople.com") ||
        hostname.endsWith(".chatlayer.ai") ||
        hostname.endsWith(".mmd.zone") ||
        hostname.endsWith(".wbcsrv.com") ||
        hostname == "localhost"
      );
    };

    const placeholderRegEx = new RegExp(REQUEST_TOKEN_PLACEHOLDER, "g");
    const replaceToken = (
      value: string,
      session:
        | {
            accessToken?: string | undefined;
          }
        | undefined
    ) =>
      value?.replace?.(placeholderRegEx, session?.accessToken ?? "N/A") ??
      value;

    //////////////////////////////////////////////////////////////////////////////
    // Patch fetch
    window.fetch = async (input, init) => {
      const patchedRequest = new Request(input, init);

      if (matchUrlForToken(patchedRequest)) {
        const session = await this.getSession();
        patchedRequest.headers.forEach((value, name) => {
          patchedRequest.headers.set(name, replaceToken(value, session));
        });
      }

      return originalFetch(patchedRequest);
    };

    //////////////////////////////////////////////////////////////////////////////
    // Patch XMLHttpRequest
    const { setRequestHeader: xhrSetHeader, send: xhrSend } =
      XMLHttpRequest.prototype;

    XMLHttpRequest.prototype.setRequestHeader = function (name, value) {
      // Can't do async call here, so we save the info in a symbol and set the actual header in send
      if (placeholderRegEx.test(value)) {
        this[tokenHeader] = [name, value];
        return;
      }

      return xhrSetHeader.call(this, name, value);
    };

    XMLHttpRequest.prototype.send = async function (body) {
      if (this[tokenHeader]) {
        const session = await getCurrentUserSessionAsync();

        xhrSetHeader.call(
          this,
          this[tokenHeader][0],
          replaceToken(this[tokenHeader][1], session)
        );
      }
      return xhrSend.call(this, body);
    };

    //////////////////////////////////////////////////////////////////////////////
    // Patch WebSocket
    const { send } = WebSocket.prototype;
    WebSocket.prototype.send = function (data) {
      return send.call(
        this,
        // Can't be async...
        // TODO: figure out how to handle expired tokens here.
        typeof data == "string"
          ? replaceToken(data, {
              accessToken: getCurrentUserSession()?.accessToken,
            })
          : data
      );
    };

    // reset to original
    return () => {
      window.fetch = originalFetch;
      XMLHttpRequest.prototype.setRequestHeader = xhrSetHeader;
      XMLHttpRequest.prototype.send = xhrSend;
      WebSocket.prototype.send = send;
    };
  };

  getSession = async (): Promise<Partial<MMUserSessionType> | undefined> => {
    // Is checking for the path necessary or maybe we could just skip it and
    // handle any urls that has a token in the param?
    if (this.checkIsLoginFlow()) return;

    if (this.isSessionValid) {
      return this.userSession;
    }

    return this.fetchNewToken();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  exchangeToken = (): any => console.error("Not implemented");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  forceInitWithExchangedToken = (): any => console.error("Not implemented");
  private getSavedToken = () =>
    getLocalStorage(REFRESH_TOKEN, isStringOrNull, null);

  private decorateUserSession = (
    authData: MMUserRefreshResponse
  ): MMUserSessionType => {
    const refreshToken =
      authData.refresh_token ||
      this.userSession?.refreshToken ||
      this.getSavedToken();

    if (!refreshToken) {
      throw new Error("Cannot store session: refreshToken not found.");
    }
    return {
      accessToken: authData.access_token,
      refreshToken,
      expireIn: authData.expires_in,
      createdAt: Math.floor(Date.now() / 1000),
    };
  };

  private storeSession = (response: MMUserRefreshResponse) => {
    this.userSession = this.decorateUserSession(response);

    return this.userSession;
  };

  private handleAuthResponse = async (response: Response) => {
    const authData = (await response.json()) as MMUserRefreshResponse;
    this.storeSession(authData);
    return authData;
  };

  private fetchNewToken = (refreshToken?: string) => {
    // If already fetching, return current promise
    if (this.accessTokenUpdatePromise) {
      return this.accessTokenUpdatePromise;
    }
    const refresh_token = refreshToken || this.getSavedToken();

    if (!refresh_token) {
      console.warn("No Refresh token found! Have you logged in?");
      return;
    }

    const body = JSON.stringify({
      grant_type: "refresh_token",
      refresh_token,
    });
    this.accessTokenUpdatePromise = originalFetch(
      `${getMessageMediaApiUrlMiddleware()}/iam/v1/jwt/auth`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body,
      }
    ).then(async (res) => {
      this.accessTokenUpdatePromise = undefined;
      if (!res.ok) {
        if (res.status === 401)
          throw new Error("Couldn't retrieve refresh token: not-authenticated");
        throw new Error(`Couldn't retrieve refresh token ${await res.text()}`);
      }
      await this.handleAuthResponse(res);
      return { ...this.userSession };
    });
    return this.accessTokenUpdatePromise;
  };

  private parseToken = (token: string) => {
    try {
      return JSON.parse(token);
    } catch (error) {
      console.error("Failed to parse refresh token", error);
      return token;
    }
  };

  private get isSessionValid() {
    return Boolean(
      this.userSession &&
        this.userSession.accessToken &&
        this.userSession.createdAt &&
        this.userSession.expireIn &&
        tokenSecondsLeft(this.userSession) - 10 > 0
    );
  }

  private checkIsLoginFlow = () => {
    const urlParams = new URLSearchParams(location.search);

    return (
      loginHubPaths.includes(location.pathname) &&
      (urlParams.get("token") || urlParams.get("tokenId"))
    );
  };
}
